/**
 * ErrorBoundary global + reporter HTTP (PR #43).
 *
 * Captura erros não-tratados na árvore React e reporta ao backend via
 * `POST /api/v1/client-errors`. UI degradada amigável + botão de reload.
 *
 * Companheiro: `setupGlobalErrorReporting()` em `lib/errorReporter.ts`
 * — registra window.onerror e unhandledrejection no startup.
 */
import { cn } from "@/lib/utils";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";
import { reportClientError } from "@/lib/errorReporter";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorReported: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorReported: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorReported: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Reporta ao backend (best-effort, não bloqueia render do fallback)
    reportClientError({
      origem: "boundary",
      mensagem: error.message || "Unknown error",
      stack: error.stack,
      component_stack: info.componentStack ?? undefined,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    }).finally(() => {
      this.setState((s) => ({ ...s, errorReported: true }));
    });

    // Mantém log local pra debug (console)
    if (typeof console !== "undefined") {
      console.error("[ErrorBoundary] capturou:", error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      const errorMsg = this.state.error?.message || "Erro inesperado";
      const stack = this.state.error?.stack || "";

      return (
        <div
          className="flex items-center justify-center min-h-screen p-6 bg-background"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex flex-col items-center w-full max-w-2xl p-8 rounded-2xl border border-white/8 bg-white/[0.02]">
            <AlertTriangle
              size={48}
              className="text-amber-400 mb-6 flex-shrink-0"
              aria-hidden="true"
            />

            <h2 className="text-xl text-white font-semibold mb-2">
              Algo deu errado.
            </h2>
            <p className="text-sm text-white/60 mb-6 text-center max-w-md">
              Encontramos um erro inesperado. Nossa equipe foi avisada e está
              investigando. Você pode tentar recarregar a página ou voltar pro
              início.
            </p>

            {/* Detalhes técnicos colapsáveis */}
            <details className="w-full mb-6">
              <summary className="text-xs text-white/40 cursor-pointer hover:text-white/60 select-none">
                Ver detalhes técnicos
              </summary>
              <div className="mt-3 p-4 rounded-lg bg-black/40 overflow-auto max-h-64">
                <p className="text-xs text-amber-400 font-mono mb-2">
                  {errorMsg}
                </p>
                <pre className="text-[10px] text-white/40 whitespace-pre-wrap break-words">
                  {stack}
                </pre>
              </div>
            </details>

            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  "flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg",
                  "bg-cyan-500 text-slate-900 font-semibold text-sm",
                  "hover:opacity-90 transition-opacity"
                )}
              >
                <RotateCcw size={16} aria-hidden="true" />
                Recarregar página
              </button>
              <button
                onClick={() => {
                  window.location.href = "/";
                }}
                className={cn(
                  "flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg",
                  "bg-white/5 text-white/80 font-semibold text-sm border border-white/10",
                  "hover:bg-white/10 transition-colors"
                )}
              >
                <Home size={16} aria-hidden="true" />
                Voltar pro início
              </button>
            </div>

            {this.state.errorReported && (
              <p className="text-[10px] text-white/30 mt-6">
                Erro reportado pra equipe técnica.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
